import { useCallback, useEffect, useRef, useState } from 'react';
import { RequestFailed } from './api';

export type AsyncState<T> = { data: T | null; loading: boolean; error: string | null };

export function useAsync<T>(fn: () => Promise<T>, deps: unknown[] = []): AsyncState<T> & { reload: () => void } {
  const [state, setState] = useState<AsyncState<T>>({ data: null, loading: true, error: null });
  const generation = useRef(0);

  const run = useCallback(() => {
    const mine = ++generation.current;
    setState((s) => ({ ...s, loading: true, error: null }));
    fn()
      .then((data) => mine === generation.current && setState({ data, loading: false, error: null }))
      .catch((e: unknown) => {
        if (mine !== generation.current) return;
        setState({ data: null, loading: false, error: e instanceof RequestFailed ? e.message : 'Ошибка загрузки' });
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  useEffect(() => {
    run();
    return () => {
      generation.current++;
    };
  }, [run]);

  return { ...state, reload: run };
}

/** Обёртка для действий: сама показывает занятость и ошибку, чтобы не дублировать это в каждой форме. */
export function useAction<A extends unknown[]>(fn: (...args: A) => Promise<unknown>) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);

  const run = useCallback(
    async (...args: A) => {
      setBusy(true);
      setError(null);
      setOk(null);
      try {
        await fn(...args);
        setOk('Сохранено');
        setTimeout(() => setOk(null), 2500);
        return true;
      } catch (e) {
        setError(e instanceof RequestFailed ? e.message : 'Не удалось выполнить');
        return false;
      } finally {
        setBusy(false);
      }
    },
    [fn],
  );

  return { run, busy, error, ok };
}
